import React, { useState, useEffect, useRef } from 'react';
import { Droplets, Package, Shield, Settings } from 'lucide-react';

const SPECS = [
  {
    icon: Droplets,
    title: 'Generación Atmosférica',
    desc: 'Capta humedad del aire y la transforma en agua potable. Sin garrafones, sin dependencia externa. 10 a 12 litros diarios.',
  },
  {
    icon: Package,
    title: 'Cero Plástico',
    desc: 'Depósito interno en vidrio grado alimenticio certificado. Sistema sellado hermético. El agua nunca toca plástico.',
  },
  {
    icon: Shield,
    title: 'Filtración UV Multicapa',
    desc: 'Esterilización por luz UV, control bacteriológico activo, mineralización adaptable. Filtros intercambiables.',
  },
  {
    icon: Settings,
    title: 'Personalización Total',
    desc: 'pH ajustable entre 7.0 y 8.5. Agua fría y caliente. Perfil mineral configurable. 127V compatible doméstico.',
  },
];

const COUNTERS = [
  { target: 20, unit: 'L / Por día', decimals: 0 },
  { target: 8.5, unit: 'pH personalizable', decimals: 1 },
  { target: 127, unit: 'V / Compatible doméstico', decimals: 0 },
];

const useCounter = (target, duration, isVisible, delay, decimals) => {
  const [count, setCount] = useState(0);
  const [showUnit, setShowUnit] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    const startDelay = setTimeout(() => {
      const startTime = performance.now();
      let raf;
      const animate = (now) => {
        const t = Math.min((now - startTime) / (duration * 1000), 1);
        const eased = 1 - Math.pow(1 - t, 3);
        setCount(eased * target);
        if (t < 1) {
          raf = requestAnimationFrame(animate);
        } else {
          setShowUnit(true);
        }
      };
      raf = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(raf);
    }, delay);
    return () => clearTimeout(startDelay);
  }, [isVisible, target, duration, delay, decimals]);

  return { count, showUnit };
};

const CounterItem = ({ target, unit, decimals, isVisible, delay }) => {
  const { count, showUnit } = useCounter(target, 2.2, isVisible, delay, decimals);
  const display = decimals > 0 ? count.toFixed(1) : Math.round(count).toString();

  return (
    <div
      className="liquid-glass-dark"
      style={{ textAlign: 'center', flex: 1, padding: '32px 24px', minWidth: '160px' }}
      data-testid={`counter-${target}`}
    >
      <div style={{
        fontFamily: 'Inter, sans-serif', fontWeight: 800,
        fontSize: 'clamp(36px, 4vw, 64px)',
        letterSpacing: '-0.03em', color: '#FFFFFF',
        lineHeight: 1,
      }}>
        {display}
      </div>
      <div
        className={`counter-unit ${showUnit ? 'show' : ''}`}
        style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300,
          fontSize: '14px', letterSpacing: '0.06em', color: '#6B6B6B',
          marginTop: '12px',
        }}
      >
        {unit}
      </div>
    </div>
  );
};

const SpecsSection = () => {
  const gridRef = useRef(null);
  const counterRef = useRef(null);
  const [gridVisible, setGridVisible] = useState(false);
  const [counterVisible, setCounterVisible] = useState(false);

  useEffect(() => {
    const obs1 = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setGridVisible(true); obs1.disconnect(); } },
      { threshold: 0.1 }
    );
    const obs2 = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setCounterVisible(true); obs2.disconnect(); } },
      { threshold: 0.3 }
    );
    if (gridRef.current) obs1.observe(gridRef.current);
    if (counterRef.current) obs2.observe(counterRef.current);
    return () => { obs1.disconnect(); obs2.disconnect(); };
  }, []);

  return (
    <section
      data-testid="specs-section"
      style={{ background: '#F5F5F7' }}
    >
      {/* Specs grid */}
      <div style={{ padding: 'clamp(80px,10vw,120px) clamp(24px,8vw,120px)' }}>
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <div className="section-label" style={{
            opacity: gridVisible ? 1 : 0,
            transition: 'opacity 700ms ease-out',
          }}>
            Tecnología
          </div>
          <h2 style={{
            fontFamily: 'Inter, sans-serif', fontWeight: 700,
            fontSize: 'clamp(28px, 4vw, 56px)',
            letterSpacing: '-0.02em', color: '#0A0A0A',
            opacity: gridVisible ? 1 : 0,
            transform: gridVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 700ms ease-out 100ms, transform 700ms ease-out 100ms',
            willChange: 'transform',
          }}>
            Construida sin concesiones.
          </h2>
        </div>

        <div
          ref={gridRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '48px',
            maxWidth: '1100px',
            margin: '0 auto',
          }}
        >
          {SPECS.map((spec, i) => {
            const Icon = spec.icon;
            return (
              <div
                key={i}
                data-testid={`spec-block-${i}`}
                className="liquid-glass"
                style={{
                  padding: '32px',
                  opacity: gridVisible ? 1 : 0,
                  transform: gridVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: `opacity 700ms ease-out ${i * 100 + 200}ms, transform 700ms ease-out ${i * 100 + 200}ms`,
                  willChange: 'transform',
                }}
              >
                <Icon size={28} style={{ color: '#0EA5E9', marginBottom: '20px' }} />
                <div style={{
                  fontFamily: 'Inter, sans-serif', fontWeight: 700,
                  fontSize: '18px', color: '#0A0A0A', marginBottom: '12px',
                }}>
                  {spec.title}
                </div>
                <p style={{
                  fontFamily: 'Inter, sans-serif', fontWeight: 300,
                  fontSize: '15px', lineHeight: 1.7, color: '#6B6B6B',
                }}>
                  {spec.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Counter strip */}
      <div
        ref={counterRef}
        style={{
          background: '#0A0A0A',
          padding: 'clamp(60px,8vw,80px) clamp(24px,6vw,80px)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '40px',
          justifyContent: 'center',
        }}
      >
        {COUNTERS.map((c, i) => (
          <CounterItem
            key={i}
            target={c.target}
            unit={c.unit}
            decimals={c.decimals}
            isVisible={counterVisible}
            delay={i * 700}
          />
        ))}
      </div>
    </section>
  );
};

export default SpecsSection;
